import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { get } from 'api/fetch';
import { dedupeRequest } from '../utils';
import { CANCELLATION_REASON } from '../constants';

export const fetchLastCancelledContract = createAsyncThunk(
  'nomad_insurance/contract/fetchLastCancelledContract',
  async () => {
    const response = await get(
      `/contracts/last-cancelled?cancellationReasons=${CANCELLATION_REASON.MODIFY_ADDON},${CANCELLATION_REASON.MODIFY_GROUP_MEMBER},${CANCELLATION_REASON.WRONG_START_DATE}`,
      { isNIOrchestrator: true }
    );
    return (response || [])[0];
  }
);

// ================== Reducers ================== //

const slice = createSlice({
  name: 'nomad_insurance/contract',
  initialState: {
    isLoading: false,
    error: null,
    // activePaidContracts: all paid contracts active now or in the future
    activePaidContracts: null,
    activeContract: null,
    contractFetchTimestamp: null,
    // allActiveContracts: all contracts active now or in the future including contracts with pending payment
    allActiveContracts: null,
    // contractsIncludingExpired: includes contracts even after passed end date
    contractsIncludingExpired: null,
    // allPaidContracts: includes contracts even after passed end date but paid
    allPaidContracts: null,
    // ongoingContract: contract that is currently ongoing (startDate <= today <= endDate)
    ongoingContract: null,
    lastCancelledContract: {
      isLoading: null,
      contract: null,
      error: null
    }
  },
  reducers: {
    // fetchContracts
    fetchContractsRequest: (state) => {
      state.isLoading = true;
      state.error = null;
      state.contractFetchTimestamp = Date.now();
    },
    fetchContractsSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.allActiveContracts = [...payload.present, ...payload.future];
      state.activePaidContracts = state.allActiveContracts.filter((item) => item.isPaid);
      state.contractsIncludingExpired = [
        ...payload.past,
        ...payload.present,
        ...payload.future
      ];
      state.allPaidContracts = state.contractsIncludingExpired.filter(
        (item) => item.isPaid
      );
      state.activeContract = payload.contractUsed;
      state.ongoingContract = payload.active;
    },
    fetchContractsFailure: (state, { payload }) => {
      state.isLoading = false;
      state.error = payload;
      state.activePaidContracts = null;
      state.activeContract = null;
      state.allActiveContracts = null;
      state.allPaidContracts = null;
      state.contractsIncludingExpired = null;
      state.ongoingContract = null;
    }
  },
  extraReducers: {
    [fetchLastCancelledContract.pending]: (state) => {
      state.lastCancelledContract.isLoading = true;
      state.lastCancelledContract.error = null;
    },
    [fetchLastCancelledContract.fulfilled]: (state, { payload }) => {
      state.lastCancelledContract.isLoading = false;
      state.lastCancelledContract.contract = payload;
    },
    [fetchLastCancelledContract.rejected]: (state, action) => {
      state.lastCancelledContract.isLoading = false;
      state.lastCancelledContract.error = action.error;
    }
  }
});

export default slice.reducer;

// ================== Actions ================== //

const { actions } = slice;

export const fetchContracts = () => async (dispatch, getState) => {
  const { contractFetchTimestamp } = getState().nomadInsurance.contract;
  const shouldNotRun = dedupeRequest(contractFetchTimestamp);
  if (shouldNotRun) return;

  dispatch(actions.fetchContractsRequest());

  try {
    const res = await get(`/contracts/summary`, {
      isNIOrchestrator: true
    });

    const { past, future, active } = res;
    const contractUsed = active || future[0] || past[0];

    dispatch(
      actions.fetchContractsSuccess({
        ...res,
        contractUsed,
        active
      })
    );
  } catch (error) {
    dispatch(actions.fetchContractsFailure(error));
  }
};
